import { isDate } from 'date-fns'
import { difference, isArray, isObject, keys, isEmpty, isNumber, isBoolean, differenceBy } from 'lodash'
import { UnknownArray, UnknownObject } from '../types/index'

export interface IEntity {
  id: string
}

export interface IEntitiesDifference<T> {
  deletedEntities: T[]
  editedEntities: T[]
  createdEntities: T[]
  hasDifferences: boolean
}

export const isDefined = (value: unknown): boolean => {
  return value !== undefined && value !== null
}

export const getDifference = <T>(aObject: T, bObject: T, result: UnknownObject = {}): Partial<T> => {
  const a = (aObject ?? {}) as UnknownObject
  const b = (bObject ?? {}) as UnknownObject

  if (!isObject(a) || !isObject(b)) {
    throw new Error('args must be objects')
  }

  keys(a).forEach((key) => {
    if (isArray(a[key])) {
      if (isArray(b[key])) {
        const difference = getArrayDifference(a[key] as UnknownArray, b[key] as UnknownArray)

        if (isArray(difference)) {
          result[key] = a[key]
        }
      } else {
        result[key] = a[key]
      }
    } else if (isDate(a[key]) || isDate(b[key])) {
      if (isDate(b[key]) && isDate(a[key])) {
        if ((a[key] as Date).getTime() !== (b[key] as Date).getTime()) {
          result[key] = a[key]
        }
      } else {
        result[key] = a[key]
      }
    } else if (isObject(a[key])) {
      if (isObject(b[key])) {
        const difference = getDifference(a[key] as UnknownObject, b[key] as UnknownObject)
        if (!isEmpty(difference)) {
          result[key] = difference
        }
      } else {
        result[key] = a[key]
      }
    } else if ((isDefined(a[key]) || isDefined(b[key])) && a[key] !== b[key]) {
      result[key] = a[key]
    }
  })

  return result as Partial<T>
}

const getArrayDifference = <T>(a: T[] = [], b: T[] = []): T[] | null => {
  if (a.length !== b.length) {
    return a
  } else if (a.length > 0) {
    if (isObject(a[0])) {
      const arrayDiff = a
        .map((_: T, index: number) => {
          return getDifference<T>(a[index], (b[index] ?? {}) as T)
        })
        .filter((hash: unknown) => {
          return !isEmpty(hash)
        })

      if (isEmpty(arrayDiff)) {
        return null
      }

      return a
    } else if (difference(a, b).length > 0 || difference(b, a).length > 0) {
      return a
    }
  }

  // a and b are empty

  return null
}

export const filterEmptyValue = <T>(partialObj: Partial<T>): Partial<T> => {
  const obj = (partialObj ?? {}) as UnknownObject
  let result: Partial<T> = {}

  keys(obj).forEach((key) => {
    const hasValue =
      isNumber(obj[key]) ||
      isBoolean(obj[key]) ||
      (isArray(obj[key]) && !isEmpty(obj[key])) ||
      (!isArray(obj[key]) && !!obj[key])

    if (hasValue) {
      result = {
        ...result,
        [key]: obj[key]
      }
    }
  })

  return result
}

export const getEntitiesDifference = <T extends Partial<IEntity>>(
  newEntities: T[],
  oldEntities: T[]
): IEntitiesDifference<T> => {
  const deletedEntities = differenceBy(oldEntities, newEntities, 'id')
  const createdEntities = newEntities.filter((entity) => !entity.id)
  const editedEntities = newEntities.filter((entity) => {
    if (!entity.id) {
      return false
    }
    const oldEntity = oldEntities.find(({ id }) => id === entity.id)
    return !isEmpty(getDifference(entity, oldEntity))
  })

  return {
    deletedEntities,
    editedEntities,
    createdEntities,
    hasDifferences: !isEmpty(deletedEntities) || !isEmpty(createdEntities) || !isEmpty(editedEntities)
  }
}
